/**
 * Shared auth interceptor core.
 *
 * Single source of truth for attaching auth/device headers, running the
 * token-refresh mutex, and handling hard auth failures. Consumed by both the
 * axios interceptor (`services/axios.ts`) and the CopilotKit fetch wrapper
 * (`services/copilotFetch.ts`) so the two layers cannot drift apart.
 */

import { getApiToken, logout, refreshAccessToken } from "@services/auth";
import { AuthBlockReason, emitAuthBlocked } from "@services/authEvents";
import { getDeviceIdentity } from "@services/deviceFingerprint";
import { AUTH_ENABLED } from "@services/env";

/**
 * localStorage key that Radar administrators can set (via DevTools) to act as
 * another user. When present and non-blank, its value is sent as the
 * `X-On-Behalf-Of` header on every request; the backend applies it only when
 * the caller is a verified administrator, and ignores it otherwise.
 *
 *   localStorage.setItem("radarplus_on_behalf_of", "someuser")   // start
 *   localStorage.removeItem("radarplus_on_behalf_of")            // stop
 */
const IMPERSONATE_STORAGE_KEY = "radarplus_on_behalf_of";
const IMPERSONATE_HEADER = "X-On-Behalf-Of";

/**
 * Return a copy of `headers` with a fresh auth token (pulled per-request) and
 * best-effort device-identity headers attached.
 *
 * - When AUTH_ENABLED, `getApiToken()` self-refreshes within its 60s buffer and picks the token
 *   type the caller's provider expects — access_token for Federate/IdPrism, id_token for Cognito.
 * - Device identity is best-effort: a failure is logged and skipped.
 */
export async function attachAuthHeaders(headers: Record<string, string>): Promise<Record<string, string>> {
  const next: Record<string, string> = { ...headers };

  if (AUTH_ENABLED) {
    const token = await getApiToken();
    if (token) {
      next.Authorization = `Bearer ${token}`;
    }
  }

  try {
    const { deviceId, fingerprint } = await getDeviceIdentity();
    next["X-Device-Id"] = deviceId;
    next["X-Device-Fingerprint"] = fingerprint;
  } catch (err) {
    console.warn("Failed to attach device identity headers:", err);
  }

  // Admin-only impersonation: forward the target user if one is set in localStorage.
  // The backend enforces that only administrators may act on this header.
  try {
    const onBehalfOf = localStorage.getItem(IMPERSONATE_STORAGE_KEY);
    if (onBehalfOf?.trim()) {
      next[IMPERSONATE_HEADER] = onBehalfOf.trim();
    }
  } catch (err) {
    console.warn("Failed to read impersonation override:", err);
  }

  return next;
}

// ============================================================================
// Refresh mutex (shared by axios + CopilotKit)
// ============================================================================

let isRefreshing = false;
// eslint-disable-next-line no-unused-vars
let refreshSubscribers: ((token: string | null) => void)[] = [];

function notifySubscribers(token: string | null): void {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

/**
 * Refresh the token at most once concurrently. Concurrent callers await the
 * single in-flight refresh.
 *
 * Resolves to the token the caller's provider authenticates with — the access_token for
 * Federate/IdPrism, the id_token for Cognito — or null on failure. The axios 401 handler puts
 * this value straight into the retried request's `Authorization` header, so it must be the same
 * kind of token {@code attachAuthHeaders} sends; returning an id_token here while the backend
 * expects an access_token would make every retry 401 and log the user out.
 */
export async function refreshAuthOnce(): Promise<string | null> {
  if (isRefreshing) {
    return new Promise<string | null>(resolve => {
      refreshSubscribers.push(resolve);
    });
  }

  isRefreshing = true;
  console.info("Attempting token refresh");
  try {
    const refreshed = await refreshAccessToken();
    const token = refreshed ? await getApiToken() : null;
    notifySubscribers(token);
    return token;
  } catch (err) {
    console.error("Token refresh threw an error:", err);
    notifySubscribers(null);
    return null;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Centralized hard-failure handling: broadcast the auth-block event and log out.
 */
export function failAuth(reason: AuthBlockReason, message?: string): void {
  emitAuthBlocked({ reason, message });
  logout();
}

/** Test-only: reset the refresh-mutex state so tests start from a clean slate. */
export function __resetAuthInterceptorForTests(): void {
  isRefreshing = false;
  refreshSubscribers = [];
}
